/**
 * Shared Three.js loader
 *
 * Ensures only one instance of Three.js is loaded across all 3D components.
 * This prevents the "Multiple instances of Three.js being imported" warning.
 */

export type ThreeModule = typeof import("three");
export type OrbitControlsType =
  typeof import("three/examples/jsm/controls/OrbitControls.js").OrbitControls;

interface ThreeLoaderResult {
  THREE: ThreeModule;
  OrbitControls: OrbitControlsType;
}

// Cached promise to ensure single load
let loadPromise: Promise<ThreeLoaderResult> | null = null;

/**
 * Load Three.js and OrbitControls
 * Returns cached instance if already loaded
 */
export async function loadThree(): Promise<ThreeLoaderResult> {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    const [THREE, { OrbitControls }] = await Promise.all([
      import("three"),
      import("three/examples/jsm/controls/OrbitControls.js"),
    ]);

    return { THREE, OrbitControls };
  })();

  return loadPromise;
}

/**
 * Check if Three.js is already loaded
 */
export function isThreeLoaded(): boolean {
  return loadPromise !== null;
}
